<script setup>
import { VueQueryDevtools } from '@tanstack/vue-query-devtools'
import { storeToRefs } from 'pinia'
import { useQueryClient } from '@tanstack/vue-query'
import { useMessageStore } from '~/stores/messages'
import { useSideBarStore } from '~/stores/sideBar'
import { useAuthStore } from '~/stores/auth'
import { signIn, signOut } from '~/composables/useMsal'
import { useModalStore } from '~/stores/modal'

// Modal store
const modalStore = useModalStore()
const { modalOpen, modalTitle, modalCopy } = storeToRefs(modalStore)
const { toggleModal } = modalStore

// Fetch query client
const queryClient = useQueryClient()

// Define locale
const { setLocale, setLocaleMessage } = useI18n()

// Fetch runtime config
const config = useRuntimeConfig()

// Fetch some global states
const onBoarding = useState('state_showOnboarding')
const bearerToken = useState('state_bearerToken')
const oid = useState('state_userOid')

// Fetch route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Fetch auth store
const authStore = useAuthStore()
const { isLoggedIn } = storeToRefs(authStore)

// Fetch sidebar store
const sideBarStore = useSideBarStore()
const { isSideBarLayerOpen } = storeToRefs(sideBarStore)
const { toggleSideBarLayer } = sideBarStore

// Fetch messages
const messagesStore = useMessageStore()
const { updateMessages } = messagesStore

const { data: staticKeys, suspense: messageSuspense } = useFetchMessages()

// Fetch a center list ito check if center route is valid
const { data: centers, suspense: centerSuspense } = useFetchCenterList()

onServerPrefetch(async () => {
    await centerSuspense()
    await messageSuspense()

    // Update messages immediately on the server
    updateMessages(staticKeys.value)
})

const isCenterPage = computed(() => centers.value?.some(item => item.uid === centerRoute.value))

// Get center details
const { data: centerDetails, suspense: centerDetailSuspense } = useFetchCenterDetails(centerRoute.value)

onServerPrefetch(async () => {
    if (isCenterPage.value) {
        await centerDetailSuspense()
    }
})

// Load messages into i18n
setLocaleMessage('de', staticKeys.value)

// Get query params to content map
const queryParamsToContentMap = config?.public?.queryParamsToContentMap

// Now we need to check if we have to react on queries that require auth
const requireAuth = getKeysByCondition(queryParamsToContentMap, 'auth')

/**
 * Watcher for user data
 * Reacts on invalid user account
 * If onboarding is true and firstName is not set, show onboarding layer
 *
 **/
watch(
    () => isLoggedIn.value,
    async (newLoggedIn) => {
        if (newLoggedIn) {
            // Get user data
            const { data: user, suspense: userSuspense } = useFetchProfile({ bearerToken, oid })

            await userSuspense()

            // Special treatment for an invalid account
            if ((user.value?.status === 'invalidAccount') && import.meta.client) {
                await signOut(route, `${route.path}?support`)
            }

            // Compute current user
            const currentUser = computed(() => user.value?.data)

            // Check if user has to be onboarded
            if (!currentUser.value?.firstName && onBoarding.value) {
                await nextTick()

                toggleSideBarLayer(true)
            }
        } else {
            // User logged out: handle query deletions
            queryClient.removeQueries({
                predicate: (query) => {
                    const queryKey = query.queryKey

                    // Exact match for ['user', oid.value]
                    if (queryKey.length === 2 && queryKey[0] === 'user' && queryKey[1] === oid.value) {
                        return true
                    }

                    // Match patterns for dynamic queries
                    return (
                        (queryKey.includes('shopCouponList') || queryKey.includes('couponList')) &&
                        queryKey.includes(oid.value)
                    )
                }
            })
        }
    },
    { immediate: true })

/**
 * Watcher for query parameters
 *  If a user is on center page and there are query parameters that require auth trigger login
 * I do not want to do this in middleware because you need full url for azure login.
 * (maybe a better solution would be to pass it through state)
 *
 */
watch(
    () => route.query, // Watch the query parameters
    async (query) => {
        const queryParamExistsInRequireAuth = hasAnyQueryParam(requireAuth, query)

        // Call signIn if the conditions are met
        if (queryParamExistsInRequireAuth && isCenterPage.value && (!isLoggedIn.value) && import.meta.client) {
            await signIn({
                center: centerRoute.value,
                centerName: centerDetails.value?.centerName
            })
        }
    },
    { immediate: true }
)

/**
 * Watcher for the isSideBarLayerOpen property
 * If onboarding Layer is shown, display of alle sidebar types is prevented.
 * When a user closes the onboarding layer, we need to react to query params
 *
 */
watch(
    () => isSideBarLayerOpen.value,
    async (newValue) => {
        const queryParams = route.query
        const queryParamsArray = Object.keys(queryParams) || []
        const queryParamExist = queryParamsArray.length > 0
        const reactToQueryParams = Object.keys(queryParamsToContentMap) || []
        const queryParamExistsInReactToParams = hasAnyQueryParam(reactToQueryParams, queryParams)

        if (!newValue) {
            if (queryParamExist && queryParamExistsInReactToParams) {
                // Define an object mapping query keys to sidebar configurations

                const contentConfig = getMappedContentConfig(queryParamsToContentMap)

                // Iterate over the query keys and check various use cases
                for (const queryKey of Object.keys(contentConfig)) {
                    if (queryParams[queryKey] === undefined) continue // Early return if the query param is not defined

                    const { open, content, sideBarType } = contentConfig[queryKey]

                    handleSidebar(open, content, sideBarType)
                }
            }
        }
    },
)
</script>

<template>
    <PageMainWrapper>
        <PageHeader />
        <SideBar />
        <UserAccountOnBoarding />

        <slot />

        <Transition name="fade">
            <ModalWindow :visible="modalOpen" @update:toggle-dialog="toggleModal">
                <LazyAppDownloadContent
                    :app-download-content-config="{
                        title: modalTitle,
                        description: modalCopy
                    }"
                />
            </ModalWindow>
        </Transition>
        <PageFooter />

        <!-- after the footer: PageMainWrapper is an auto/1fr/auto grid, a dev-only node earlier in the flow takes the 1fr row -->
        <DevOnly>
            <!-- Show query devtools -->
            <VueQueryDevtools />

            <!-- Set locale for testing purposes -->
            <div style="position:fixed; top: 0; right: 0; z-index: 999">
                <button @click="setLocale('de')">
                    DE
                </button>
                <button @click="setLocale('en')">
                    EN
                </button>
            </div>
        </DevOnly>
    </PageMainWrapper>
</template>
